import React from "react";
import ReactMarkdown from "react-markdown";
import { Copy, Edit3, RefreshCw, Save, Sparkles, X } from "lucide-react";
import { fmtDate } from "./adminUtils";

/** The primary "AI Kickoff Brief" column. Presentational — all state +
 *  side-effects live in the parent page and are passed in as handlers. */
export default function AiBriefPanel({
  doc,
  editing,
  draft,
  setDraft,
  genLoading,
  savingEdit,
  onGenerate,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onCopy,
}) {
  return (
    <section className="rounded-3xl border border-sand-300 bg-sand-50 p-6 sm:p-8" data-testid="ai-brief-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-clay/10 text-clay border border-clay/30">
            <Sparkles size={14} />
          </span>
          <h2 className="font-display text-[1.25rem] font-semibold tracking-tight">AI Kickoff Brief</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {doc?.ai_brief && !editing && (
            <>
              <button
                onClick={onCopy}
                data-testid="ai-brief-copy"
                className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-sand-100 px-3 py-1.5 text-[12px] text-ink hover:border-clay/50 transition-colors min-h-[32px]"
              >
                <Copy size={12} /> Copy
              </button>
              <button
                onClick={onStartEdit}
                data-testid="ai-brief-edit"
                className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-sand-100 px-3 py-1.5 text-[12px] text-ink hover:border-clay/50 transition-colors min-h-[32px]"
              >
                <Edit3 size={12} /> Edit
              </button>
            </>
          )}
          <button
            onClick={onGenerate}
            disabled={genLoading}
            data-testid="ai-brief-generate"
            className="inline-flex items-center gap-1.5 rounded-full bg-ink-deep text-sand-100 px-4 py-1.5 text-[12px] font-medium hover:bg-rust transition-colors min-h-[32px] disabled:opacity-60"
          >
            {genLoading ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {doc?.ai_brief ? "Regenerate" : "Generate"}
          </button>
        </div>
      </div>

      {doc?.ai_brief_generated_at && (
        <p className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.16em] text-ink-muted">
          {doc.ai_brief_model || "gpt-5.4"} · generated {fmtDate(doc.ai_brief_generated_at)}
          {doc.ai_brief_edited && doc.ai_brief_edited_at && ` · edited ${fmtDate(doc.ai_brief_edited_at)}`}
        </p>
      )}

      {editing ? (
        <div className="mt-5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={28}
            data-testid="ai-brief-textarea"
            className="w-full rounded-2xl border border-sand-300 bg-sand-100 p-4 font-mono text-[12.5px] text-ink leading-relaxed focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/20"
          />
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={onSaveEdit}
              disabled={savingEdit || !draft.trim()}
              data-testid="ai-brief-save"
              className="inline-flex items-center gap-1.5 rounded-full bg-olive text-sand-100 px-4 py-2 text-[13px] font-medium hover:opacity-90 transition-opacity min-h-[38px] disabled:opacity-60"
            >
              {savingEdit ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />} Save edits
            </button>
            <button
              onClick={onCancelEdit}
              data-testid="ai-brief-cancel"
              className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-sand-100 px-4 py-2 text-[13px] text-ink-muted hover:text-ink transition-colors min-h-[38px]"
            >
              <X size={13} /> Cancel
            </button>
          </div>
        </div>
      ) : doc?.ai_brief ? (
        <article className="mt-5 prose prose-sm max-w-none text-ink
          prose-headings:font-display prose-headings:tracking-tight prose-headings:text-ink
          prose-h2:text-[1.1rem] prose-h2:mt-6 prose-h2:mb-3
          prose-h3:text-[0.95rem] prose-h3:mt-4 prose-h3:mb-2
          prose-p:text-[13.5px] prose-p:leading-relaxed prose-p:text-ink
          prose-li:text-[13.5px] prose-li:my-0.5
          prose-strong:text-ink prose-a:text-clay hover:prose-a:text-rust
          prose-code:text-clay prose-code:bg-sand-100 prose-code:px-1 prose-code:rounded"
          data-testid="ai-brief-content">
          <ReactMarkdown>{doc.ai_brief}</ReactMarkdown>
        </article>
      ) : (
        <div className="mt-6 rounded-2xl border border-dashed border-sand-300 bg-sand-100/60 p-6 text-center">
          <p className="font-display text-[1.05rem] font-semibold text-ink">No brief yet</p>
          <p className="mt-2 text-[13px] text-ink-muted leading-relaxed">
            Click <span className="font-medium text-ink">Generate</span> to turn this submission into a polished project kickoff doc.
          </p>
        </div>
      )}
    </section>
  );
}
